// Belegungs-Plantafel (W10) — reine, I/O-freie Raster-Logik (testbar ohne DOM).
// Positionierbar ist ein Auftrag nur mit vollständigem Slot (Tag + Start/Ende + gültigem Arbeitsplatz);
// alles andere mit Tagesbezug landet ehrlich in der „Nicht eingeplant"-Spalte statt still zu verschwinden.
// Datums-Arithmetik ausschließlich über lokale Helfer (localYmd) — NIE toISOString (UTC-Mitternachts-Bug).

import { bayUtilization, type OrderListItemDTO } from '@wf/shared'
import { localYmd } from '../orders/workshop'

/** Ein im Raster platzierbarer Auftrag: Slot vollständig, Arbeitsplatz im gültigen Bereich (1..bays). */
export interface PlacedOrder<T extends OrderListItemDTO = OrderListItemDTO> {
  order: T
  bay: number
  startMin: number
  endMin: number
  /** Spur innerhalb überlappender Termine desselben Platzes (0-basiert) + Spuranzahl des Clusters. */
  lane: number
  laneCount: number
}

export interface PlanDay<T extends OrderListItemDTO = OrderListItemDTO> {
  /** Platzierte Termine je Arbeitsplatz (Index 0 = Platz 1), inkl. Überlapp-Spuren. */
  byBay: PlacedOrder<T>[][]
  /** Aufträge des Tages OHNE vollständigen Slot (kein/ungültiger Platz oder fehlende Zeiten). */
  unplaced: T[]
  /** Alle Tages-Slots mit Zeiten (auch ohne Platz) — Basis der Tages-Auslastung (Parität zum Backend-Zähler). */
  slotCount: number
  utilizationPercent: number | null
}

/** „HH:MM" (Wert von <input type=time>) → Minuten ab Mitternacht; ungültig/leer → null.
 *  Minutenanteil > 59 wird abgewiesen (ein type=time-Input liefert das nie, Programmatisches schon). */
export function parseTimeToMin(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!m) return null
  if (Number(m[2]) > 59) return null
  const min = Number(m[1]) * 60 + Number(m[2])
  return min >= 0 && min <= 1440 ? min : null
}

/** Lokale Uhrzeit als Minuten ab Mitternacht — Position der „Jetzt"-Linie im Raster. */
export function minutesOfDay(d: Date): number {
  return d.getHours() * 60 + d.getMinutes()
}

/** Tag ± n Tage als lokales YYYY-MM-DD (Date-Konstruktor mit T00:00:00 = lokale Zeit). */
export function addDays(ymd: string, days: number): string {
  const d = new Date(`${ymd}T00:00:00`)
  d.setDate(d.getDate() + days)
  return localYmd(d)
}

const WEEKDAYS_LONG = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag']

/** „Dienstag, 16.7.2026" — Überschrift der Tagesansicht. */
export function dayLabel(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00`)
  return `${WEEKDAYS_LONG[d.getDay()]}, ${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`
}

/**
 * Überlapp-Spuren eines Platzes (Interval-Partitionierung): jeder Termin bekommt die erste freie Spur;
 * `laneCount` ist die Spurenzahl seines zusammenhängenden Überlapp-Clusters, damit die Karten die
 * Spaltenbreite exakt unter sich aufteilen (2 parallele Termine → je 50 %).
 */
export function layoutLanes<T extends OrderListItemDTO>(
  slots: readonly { order: T; bay: number; startMin: number; endMin: number }[],
): PlacedOrder<T>[] {
  const sorted = [...slots].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin)
  const placed: PlacedOrder<T>[] = []
  const laneEnds: number[] = [] // Ende des letzten Termins je Spur
  let cluster: PlacedOrder<T>[] = []
  let clusterEnd = -1
  const sealCluster = () => {
    const lanes = Math.max(1, ...cluster.map((p) => p.lane + 1))
    for (const p of cluster) p.laneCount = lanes
    cluster = []
    laneEnds.length = 0
  }
  for (const s of sorted) {
    if (cluster.length > 0 && s.startMin >= clusterEnd) sealCluster() // Lücke → Cluster abgeschlossen
    let lane = laneEnds.findIndex((end) => end <= s.startMin)
    if (lane === -1) lane = laneEnds.length
    laneEnds[lane] = s.endMin
    const p: PlacedOrder<T> = { ...s, lane, laneCount: 1 }
    cluster.push(p)
    placed.push(p)
    clusterEnd = Math.max(clusterEnd, s.endMin)
  }
  if (cluster.length > 0) sealCluster()
  return placed
}

/**
 * Tagesmodell der Plantafel: Aufträge des Tages auf Plätze verteilen (mit Überlapp-Spuren), Rest in
 * „Nicht eingeplant"; Auslastung über DIESELBE Domänenfunktion wie die Chef-Statistik (bayUtilization,
 * days=1) — ein Rechenweg, keine zweite Wahrheit (Notion „Auslastung vereinheitlichen").
 */
export function buildPlanDay<T extends OrderListItemDTO>(
  orders: readonly T[],
  day: string,
  bays: number,
  openStartMin: number,
  openEndMin: number,
): PlanDay<T> {
  const ofDay = orders.filter((o) => o.scheduledDate === day)
  const slots: { order: T; bay: number; startMin: number; endMin: number }[] = []
  const unplaced: T[] = []
  let timed = 0
  const daySlots: { startMin: number; endMin: number }[] = []
  for (const o of ofDay) {
    const hasTimes = o.startMin != null && o.endMin != null && o.endMin > o.startMin
    if (hasTimes) {
      timed += 1
      daySlots.push({ startMin: o.startMin as number, endMin: o.endMin as number })
    }
    const bay = o.bay ?? null
    if (hasTimes && bay != null && bay >= 1 && bay <= bays) {
      slots.push({ order: o, bay, startMin: o.startMin as number, endMin: o.endMin as number })
    } else {
      unplaced.push(o) // kein Platz, Platz > bays (geschrumpft) oder Zeiten unvollständig
    }
  }
  const byBay: PlacedOrder<T>[][] = Array.from({ length: bays }, () => [])
  for (let bay = 1; bay <= bays; bay++) {
    byBay[bay - 1] = layoutLanes(slots.filter((s) => s.bay === bay))
  }
  return {
    byBay,
    unplaced,
    slotCount: timed,
    utilizationPercent: bayUtilization(daySlots, bays, Math.max(0, openEndMin - openStartMin), 1),
  }
}

/** Unterminierte, aktive Aufträge (kein Datum, nicht fertig/abgeholt) — Arbeitsvorrat zum Einplanen. */
export function unscheduledBacklog<T extends OrderListItemDTO>(orders: readonly T[]): T[] {
  return orders.filter((o) => o.scheduledDate == null && o.status !== 'fertig' && o.status !== 'abgeholt')
}
